import { WhDatePicker } from 'src/ui/DatePicker/WhDatePicker';

import React, { useContext, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Space, Form, Select } from 'src/antd';
import { T } from '@transifex/react';
import { useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';
import { WasteFractionTag } from 'src/ui';
import { VALIDATION_MESSAGE } from '../../../../../../../../utils/validation';

import SelectableContainersList from '../../../../../../../../new-components/SelectableContainersList';
import OrderOverview from '../../../../../../../shared/OrderOverview';
import { PropertyContext } from '../../../../../../../../context/property-context';

import { useGetPropertyContainers } from '../../../../../../../../api/property/property-containers';
import { ChangeFromTo, PrefferedDate } from '../../elements/Summary';
import { PreferredDateStyles } from '../../styles';
import { StopPropagationDiv } from '../../../../../../../shared/StopPropagationDiv';
import { createMarkUp } from '../../../../../../../../utils/createMarkup';

import { useTicketAvailableDates } from '../useTicketAvailableDates';
import { DATE_FORMAT } from '../consts';
import CustomFieldsFormSection from '../CustomFieldsFormSection';

const Content = () => {
  const { ticketTypes, id: propertyId } = useContext(PropertyContext);
  const { ticketTypeId } = useParams<{ ticketTypeId: string }>() as {
    ticketTypeId: string;
  };

  const availableDates = useTicketAvailableDates({
    propertyId,
    ticketTypeId,
  });

  const ticketType: $TSFixMe = ticketTypes?.filter(
    (e: { id: string }) => e.id === ticketTypeId
  );

  const {
    portalDateHidden,
    portalDateLabel,
    portalDateHelpText,
    portalDateRequired,
    portalDescription,
    portalNoteHidden,
    portalNoteLabel,
    portalNoteHelpText,
    portalNoteRequired,
  } = ticketType?.[0] || {};

  return (
    <>
      {portalDescription && (
        <div
          style={{ marginBottom: '1rem' }}
          dangerouslySetInnerHTML={createMarkUp(portalDescription)}
        />
      )}
      <Form.Item
        label={portalNoteLabel || <T _str="Note" />}
        name={['manageContainers', 'note']}
        tooltip={portalNoteHelpText || undefined}
        rules={[
          {
            required: portalNoteRequired,
            message: VALIDATION_MESSAGE.REQUIRED,
          },
        ]}
        hidden={portalNoteHidden}
      >
        <TextArea style={{ marginBottom: '1rem' }} />
      </Form.Item>
      <SelectableContainersList
        isPickupSchemeChange
        preName={['manageContainers', 'items']}
        renderItem={({ id, data }, checked) => {
          const {
            container: {
              container_id,
              waste_fraction: wasteFraction,
              // Not destructured inline: a container can have `pickup_setting:
              // null` (no setting configured) and destructuring null throws —
              // a default only covers `undefined`, not `null`. Read it safely.
              pickup_setting: pickupSetting,
              container_type: { name: containerType },
            },
            prices,
          } = data;
          const currentTitle = pickupSetting?.name;
          const currentValue = pickupSetting?.id;
          return {
            icon: <WasteFractionTag wasteFraction={wasteFraction} iconOnly />,
            title: (
              <>
                {wasteFraction.name}
                {' - '}
                {containerType}
              </>
            ),
            subtitle: container_id || undefined,
            addonCenter: <T _str="Current: {value}" value={currentTitle} />,
            addonEnd: (
              <StopPropagationDiv>
                <Form.Item
                  name={['manageContainers', 'items', id, 'pickupSetting']}
                  dependencies={[['manageContainers', 'items', id, 'checked']]}
                  rules={[
                    {
                      required: checked,
                      message: VALIDATION_MESSAGE.REQUIRED,
                    },
                  ]}
                >
                  <Select
                    placeholder={<T _str="Pickup setting" />}
                    style={{ width: '200px' }}
                    disabled={!checked}
                    options={prices.change_pickup_setting
                      .map((node: $TSFixMe) => ({
                        label: node.pickup_setting.name,
                        value: node.pickup_setting.id,
                      }))
                      .filter(({ value }: $TSFixMe) => value !== currentValue)}
                  />
                </Form.Item>
              </StopPropagationDiv>
            ),
            addonContent: !portalDateHidden ? (
              <StopPropagationDiv>
                <PreferredDateStyles>
                  <Form.Item
                    label={portalDateLabel || <T _str="Preferred date" />}
                    name={['manageContainers', 'items', id, 'preferredDate']}
                    tooltip={portalDateHelpText || undefined}
                    rules={[
                      {
                        required: portalDateRequired && checked,
                        message: VALIDATION_MESSAGE.REQUIRED,
                      },
                    ]}
                    hidden={portalDateHidden}
                  >
                    <WhDatePicker
                      format={DATE_FORMAT}
                      inputReadOnly
                      disabled={!checked}
                      disabledDate={(date) =>
                        !(
                          availableDates.get(id) ||
                          availableDates.get('default') ||
                          []
                        )?.includes(date.format('YYYY-MM-DD'))
                      }
                    />
                  </Form.Item>
                </PreferredDateStyles>
              </StopPropagationDiv>
            ) : undefined,
          };
        }}
      />
      <CustomFieldsFormSection />
    </>
  );
};
const BreadCrumbTitle = <T _str="Select containers" />;

const Summary = () => {
  const { id: propertyId, ticketTypesMap } = useContext(PropertyContext);
  const { ticketTypeId } = useParams<{ ticketTypeId: string }>() as {
    ticketTypeId: string;
  };
  // Match the Content step's params so React Query dedupes both calls into
  // a single cache entry / network request.
  const { data } = useGetPropertyContainers(propertyId, {
    includePrices: true,
    pricesForTicketType: ticketTypeId,
  });
  const ticketType = useMemo(
    () => ticketTypesMap?.[ticketTypeId],
    [ticketTypeId, ticketTypesMap]
  );

  const returnContainerById = (id: string) => {
    return data?.data?.filter(
      (container: $TSFixMe) => container?.container?.id === id
    )?.[0];
  };

  const values = Form.useWatch(['manageContainers']);

  const items = Object.keys(values?.items || {})
    .map((containerId) => ({
      ...values?.items?.[containerId],
      id: containerId,
    }))
    .filter((n) => n.checked)
    .map(({ id, pickupSetting, containerType, preferredDate }) => ({
      containerId: id,
      pickupSettingId: pickupSetting,
      containerTypeId: containerType,
      preferredDate,
    }));

  const { portalDateLabel, portalDateHidden } = ticketType;

  // #TODO Get container waste fraction - get previous pickup setting
  const summaryItems = items?.map((item: $TSFixMe) => {
    const container = returnContainerById(item?.containerId);

    const newPickupSetting = container?.prices?.change_pickup_setting?.filter(
      (price: { pickup_setting: { id: $TSFixMe } }) =>
        price.pickup_setting.id === item.pickupSettingId
    )?.[0];

    const oldPickupSetting = container?.prices?.change_pickup_setting?.filter(
      (price: { pickup_setting: { id: $TSFixMe } }) =>
        price.pickup_setting.id === container?.container?.pickup_setting?.id
    )?.[0];

    return {
      icon: <DeleteOutlined />,
      header: container?.container?.waste_fraction?.name,
      headerSuffix: container?.container?.container_id,
      description: (
        <Space direction="vertical">
          <ChangeFromTo
            from={oldPickupSetting?.pickup_setting?.name}
            to={newPickupSetting?.pickup_setting?.name}
          />
          <PrefferedDate
            value={item.preferredDate}
            dateText={portalDateLabel}
            dateHidden={portalDateHidden}
          />
        </Space>
      ),
      price:
        newPickupSetting?.price?.container_price > 0
          ? newPickupSetting?.price?.container_price / 100
          : 0,
      previousPrice:
        oldPickupSetting?.price?.container_price > 0
          ? oldPickupSetting?.price?.container_price / 100
          : 0,
      recurringPrice: true,
    };
  });

  return <OrderOverview header="" items={summaryItems} />;
};

export const FormChangePickupSetting = {
  Form: { BreadCrumbTitle, Content },
  Summary,
};
